import { Transition } from "@headlessui/react"
import { Form, Head, Link, usePage } from "@inertiajs/react"

import { FormField } from "@/components/form-field"
import { HeadingSmall } from "@/components/heading-small"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import AppLayout from "@/layouts/app-layout"
import SettingsLayout from "@/layouts/settings/layout"
import { identityEmailVerificationPath, settingsEmailPath } from "@/routes"
import type { BreadcrumbItem } from "@/types"

const breadcrumbs: BreadcrumbItem[] = [
  {
    title: "Email settings",
    href: settingsEmailPath(),
  },
]

export default function Email() {
  const { auth } = usePage().props

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={breadcrumbs[breadcrumbs.length - 1].title} />

      <SettingsLayout>
        <div className="space-y-6">
          <HeadingSmall
            title="Update email"
            description="Update your email address and verify it"
          />

          <Form
            method="patch"
            action={settingsEmailPath()}
            options={{
              preserveScroll: true,
            }}
            resetOnError={["password_challenge"]}
            resetOnSuccess={["password_challenge"]}
            className="space-y-6"
          >
            {({ errors, processing, recentlySuccessful }) => (
              <>
                <FormField
                  label="Email address"
                  htmlFor="email"
                  error={errors.email?.join(", ")}
                >
                  <Input
                    id="email"
                    type="email"
                    name="email"
                    className="mt-1 block w-full"
                    defaultValue={auth.user.email}
                    required
                    autoComplete="username"
                    placeholder="Email address"
                  />
                </FormField>

                {!auth.user.verified && (
                  <div>
                    <p className="text-muted-foreground -mt-4 text-sm">
                      Your email address is unverified.{" "}
                      <Link
                        href={identityEmailVerificationPath()}
                        method="post"
                        as="button"
                        className="text-foreground decoration-border underline underline-offset-4 transition-colors duration-300 ease-out hover:decoration-current!"
                      >
                        Click here to resend the verification email.
                      </Link>
                    </p>
                  </div>
                )}

                <FormField
                  label="Current password"
                  htmlFor="password_challenge"
                  error={errors.password_challenge?.join(", ")}
                >
                  <Input
                    id="password_challenge"
                    name="password_challenge"
                    type="password"
                    className="mt-1 block w-full"
                    autoComplete="current-password"
                    placeholder="Current password"
                  />
                </FormField>

                <div className="flex items-center gap-4">
                  <Button disabled={processing}>Save</Button>

                  <Transition
                    show={recentlySuccessful}
                    enter="transition ease-in-out"
                    enterFrom="opacity-0"
                    leave="transition ease-in-out"
                    leaveTo="opacity-0"
                  >
                    <p className="text-muted-foreground text-sm">Saved</p>
                  </Transition>
                </div>
              </>
            )}
          </Form>
        </div>
      </SettingsLayout>
    </AppLayout>
  )
}
